/**
 * CNC PM PWA - Data Backup & Export Service (JSON / CSV)
 * Provides full database backup/restore and inspection log CSV export
 * with mandatory \uFEFF UTF-8 BOM encoding for Thai language support in Microsoft Excel.
 * Per PROJECT.md § Architecture, Feature F6.4 & ORIGINAL_REQUEST.md § R6.
 */

import type {
  InspectionRecord,
  Machine,
  ChecklistTemplate,
  SyncQueueEntry,
  AppSetting,
} from '../../types';
import { db, getAllInspections } from '../../db';

export const UTF8_BOM = '\uFEFF';

export interface DatabaseBackupPayload {
  app?: string;
  version?: string;
  exportedAt: string;
  data?: {
    machines?: Machine[];
    templates?: ChecklistTemplate[];
    inspections?: InspectionRecord[];
    syncQueue?: SyncQueueEntry[];
    settings?: AppSetting[];
  };
  machines?: Machine[];
  templates?: ChecklistTemplate[];
  inspections?: InspectionRecord[];
  syncQueue?: SyncQueueEntry[];
  settings?: AppSetting[];
}

export interface DatabaseRestoreResult {
  success: boolean;
  importedCounts: {
    machines: number;
    templates: number;
    inspections: number;
    syncQueue: number;
    settings: number;
  };
  timestamp: string;
}

/**
 * Formats inspection records into a CSV string with mandatory UTF-8 BOM (\uFEFF)
 * and properly escaped CSV cells for Thai character support in Windows Excel.
 */
export function formatInspectionsToCsv(inspections: InspectionRecord[]): string {
  const headers = [
    'InspectionID',
    'MachineID',
    'MachineName',
    'Frequency',
    'Inspector',
    'InspectionDate',
    'OperatingHours',
    'OverallStatus',
    'DefectCount',
    'SyncStatus',
    'Comments',
  ];

  const escapeCell = (val: any): string => {
    if (val === null || val === undefined) return '""';
    let str = String(val);
    // Sanitize formula injection prefixes (=, +, -, @, or leading whitespace preceding them)
    // by prepending a single quote (') per OWASP CSV/Formula injection guidelines
    if (/^[=+\-@]/.test(str) || /^[\s\t\r\n]+[=+\-@]/.test(str)) {
      str = `'${str}`;
    }
    return `"${str.replace(/"/g, '""')}"`;
  };

  const rows = (inspections || []).map((rec) => {
    const defectCount = rec.results
      ? rec.results.filter((r) => r.status === 'NG' || r.isAbnormal).length
      : 0;

    return [
      escapeCell(rec.id),
      escapeCell(rec.machineId),
      escapeCell(rec.machineName),
      escapeCell(rec.frequency),
      escapeCell(rec.inspectorName),
      escapeCell(rec.inspectionDate),
      rec.operatingHours != null ? rec.operatingHours : 0,
      escapeCell(rec.overallStatus),
      defectCount,
      escapeCell(rec.syncStatus),
      escapeCell(rec.supervisorNotes || ''),
    ].join(',');
  });

  return UTF8_BOM + [headers.join(','), ...rows].join('\r\n');
}

/**
 * Parses a CSV string containing a UTF-8 BOM, extracting rows and checking BOM presence.
 */
export function parseCsvWithBom(csvContent: string): {
  hasBom: boolean;
  headers: string[];
  rows: string[][];
} {
  const hasBom = (csvContent || '').startsWith(UTF8_BOM);
  const cleanContent = hasBom ? csvContent.slice(UTF8_BOM.length) : csvContent;
  const lines = cleanContent.split(/\r?\n/).filter((l) => l.trim().length > 0);

  if (lines.length === 0) {
    return { hasBom, headers: [], rows: [] };
  }

  // Parse CSV line taking quotes into account
  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map(parseLine);

  return { hasBom, headers, rows };
}

/**
 * Exports all inspection logs from IndexedDB to a CSV string with UTF-8 BOM
 */
export async function exportInspectionLogsToCsv(
  inspections?: InspectionRecord[]
): Promise<string> {
  const records = inspections || (await getAllInspections());
  return formatInspectionsToCsv(records);
}

/**
 * Triggers browser file download of CSV inspection logs
 */
export async function downloadCsvInspectionLogs(
  inspections?: InspectionRecord[],
  filename?: string
): Promise<void> {
  const csvContent = await exportInspectionLogsToCsv(inspections);
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const defaultFilename = `cnc_pm_inspection_logs_${new Date().toISOString().slice(0, 10)}.csv`;

  triggerFileDownload(blob, filename || defaultFilename);
}

/**
 * Exports the complete Dexie database (all stores) into a single JSON backup string
 */
export async function exportFullDatabaseBackup(): Promise<string> {
  const [machines, templates, inspections, syncQueue, settings] = await Promise.all([
    db.machines.toArray(),
    db.templates.toArray(),
    db.inspections.toArray(),
    db.syncQueue.toArray(),
    db.settings.toArray(),
  ]);

  const backup: DatabaseBackupPayload = {
    app: 'CNC_PM_PWA',
    version: '1.0.0',
    exportedAt: new Date().toISOString(),
    data: {
      machines,
      templates,
      inspections,
      syncQueue,
      settings,
    },
    // Top-level properties for maximum compatibility across consumers
    machines,
    templates,
    inspections,
    syncQueue,
    settings,
  };

  return JSON.stringify(backup, null, 2);
}

/**
 * Triggers browser file download of JSON database backup
 */
export async function downloadJsonBackup(filename?: string): Promise<void> {
  const jsonContent = await exportFullDatabaseBackup();
  const blob = new Blob([jsonContent], { type: 'application/json;charset=utf-8;' });
  const defaultFilename = `cnc_pm_backup_${new Date().toISOString().slice(0, 10)}.json`;

  triggerFileDownload(blob, filename || defaultFilename);
}

/**
 * Restores a full database backup from a JSON string into IndexedDB stores.
 * Uses put/bulkPut semantics so existing records are updated without duplicate key failures.
 */
export async function restoreFullDatabaseBackup(
  jsonString: string
): Promise<DatabaseRestoreResult> {
  if (typeof jsonString !== 'string' || !jsonString.trim()) {
    throw new SyntaxError('JSON backup string is empty or invalid');
  }

  const parsed = JSON.parse(jsonString) as DatabaseBackupPayload;
  if (!parsed || typeof parsed !== 'object') {
    throw new SyntaxError('Backup content must be a valid JSON object');
  }

  // Support both nested .data and top-level arrays
  const machines = parsed.data?.machines || parsed.machines || [];
  const templates = parsed.data?.templates || parsed.templates || [];
  const inspections = parsed.data?.inspections || parsed.inspections || [];
  const syncQueue = parsed.data?.syncQueue || parsed.syncQueue || [];
  const settings = parsed.data?.settings || parsed.settings || [];

  const counts = {
    machines: 0,
    templates: 0,
    inspections: 0,
    syncQueue: 0,
    settings: 0,
  };

  await db.transaction('rw', [db.machines, db.templates, db.inspections, db.syncQueue, db.settings], async () => {
    if (machines.length > 0) {
      for (const m of machines) {
        await db.machines.put(m);
        counts.machines++;
      }
    }

    if (templates.length > 0) {
      for (const t of templates) {
        await db.templates.put(t);
        counts.templates++;
      }
    }

    if (inspections.length > 0) {
      for (const ins of inspections) {
        await db.inspections.put(ins);
        counts.inspections++;
      }
    }

    if (syncQueue.length > 0) {
      for (const q of syncQueue) {
        // Strip auto-increment numeric id on restore to avoid conflict
        const { id, ...entryWithoutId } = q;
        await db.syncQueue.put(q);
        counts.syncQueue++;
      }
    }

    if (settings.length > 0) {
      for (const s of settings) {
        await db.settings.put(s);
        counts.settings++;
      }
    }
  });

  return {
    success: true,
    importedCounts: counts,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Utility helper to trigger browser download of a Blob
 */
function triggerFileDownload(blob: Blob, filename: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
